import { useEffect, useRef, useState } from 'react';

function defaultNormalizeUrl(value) {
  const url = String(value || '').trim();

  if (!url) return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return url;

  return `https://${url}`;
}

function defaultIsSafeUrl(value) {
  return /^(https?:\/\/|mailto:)/i.test(String(value || '').trim());
}

function escapeAttribute(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeText(value = '') {
  return escapeAttribute(value).replace(/'/g, '&#039;');
}

function isSafeHexColor(value) {
  return /^#[0-9a-f]{6}$/i.test(String(value || '').trim());
}

function RichTextEditor({
  allowLinkWithoutSelection = false,
  ariaLabel,
  className = 'job-rich-editor',
  collapseSelectionAfterCommand = false,
  colorOptions = [],
  isSafeUrl = defaultIsSafeUrl,
  linkInputPlaceholder = 'https://example.com',
  normalizeClipboardHtml,
  normalizePlainText,
  normalizeUrl = defaultNormalizeUrl,
  onChange,
  replaceDomOnSanitize = true,
  sanitizeHtml,
  showPasteMode = false,
  surfaceClassName = 'job-rich-editor__surface',
  toolbarClassName = 'job-rich-editor__toolbar',
  value,
}) {
  const editorRef = useRef(null);
  const lastSyncedValueRef = useRef(value || '');
  const selectionRef = useRef(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkMessage, setLinkMessage] = useState('');
  const [pasteMode, setPasteMode] = useState('clean');
  const [showLinkInput, setShowLinkInput] = useState(false);

  useEffect(() => {
    const nextValue = value || '';

    if (!editorRef.current || nextValue === lastSyncedValueRef.current) {
      return;
    }

    if (nextValue !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = nextValue;
    }

    lastSyncedValueRef.current = nextValue;
  }, [value]);

  const getCurrentEditorRange = () => {
    const selection = window.getSelection();

    if (!selection || selection.rangeCount === 0 || !editorRef.current) return null;

    const range = selection.getRangeAt(0);
    return editorRef.current.contains(range.commonAncestorContainer) ? range : null;
  };

  const saveSelection = () => {
    const range = getCurrentEditorRange();

    if (!range) return false;

    selectionRef.current = range.cloneRange();
    return true;
  };

  const restoreSelection = () => {
    const range = selectionRef.current;

    if (!range || !editorRef.current?.contains(range.commonAncestorContainer)) return false;

    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  };

  const moveCursorToEditorEnd = () => {
    if (!editorRef.current) return;

    const range = document.createRange();
    range.selectNodeContents(editorRef.current);
    range.collapse(false);

    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    selectionRef.current = range.cloneRange();
  };

  const collapseSelectionToEnd = () => {
    const selection = window.getSelection();

    if (!selection || selection.rangeCount === 0 || !editorRef.current) return;

    const range = selection.getRangeAt(0);
    if (!editorRef.current.contains(range.commonAncestorContainer)) return;

    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
    selectionRef.current = range.cloneRange();
  };

  const syncValue = ({ replaceDom = false } = {}) => {
    const sanitizedHtml = sanitizeHtml(editorRef.current?.innerHTML || '');

    if (replaceDomOnSanitize && replaceDom && editorRef.current && editorRef.current.innerHTML !== sanitizedHtml) {
      editorRef.current.innerHTML = sanitizedHtml;
      moveCursorToEditorEnd();
    }

    lastSyncedValueRef.current = sanitizedHtml;
    onChange(sanitizedHtml);
  };

  const runCommand = (command, commandValue = null) => {
    editorRef.current?.focus();
    if (!getCurrentEditorRange()) restoreSelection();
    if (['bold', 'italic'].includes(command)) {
      document.execCommand('styleWithCSS', false, false);
    }
    document.execCommand(command, false, commandValue);
    if (collapseSelectionAfterCommand) {
      collapseSelectionToEnd();
    } else {
      saveSelection();
    }
    syncValue();
  };

  const applyColor = (color) => {
    if (!isSafeHexColor(color)) return;

    editorRef.current?.focus();
    if (!getCurrentEditorRange()) restoreSelection();

    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return;

    document.execCommand('styleWithCSS', false, true);
    document.execCommand('foreColor', false, color);
    document.execCommand('styleWithCSS', false, false);
    if (collapseSelectionAfterCommand) {
      collapseSelectionToEnd();
    } else {
      saveSelection();
    }
    syncValue({ replaceDom: true });
  };

  const handleInput = () => {
    saveSelection();
    syncValue();
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const clipboardHtml = event.clipboardData.getData('text/html');
    const clipboardText = event.clipboardData.getData('text/plain');
    const pastePlainText = event.shiftKey || pasteMode === 'plain';
    const htmlToInsert =
      (pastePlainText ? '' : normalizeClipboardHtml(clipboardHtml)) ||
      normalizePlainText(clipboardText) ||
      sanitizeHtml(clipboardText);

    editorRef.current?.focus();
    if (!restoreSelection()) moveCursorToEditorEnd();

    if (document.queryCommandState('bold')) document.execCommand('bold');
    if (document.queryCommandState('italic')) document.execCommand('italic');

    document.execCommand('insertHTML', false, htmlToInsert);
    if (collapseSelectionAfterCommand) {
      collapseSelectionToEnd();
    } else {
      saveSelection();
    }
    syncValue({ replaceDom: true });
  };

  const openLinkInput = () => {
    const selection = window.getSelection();
    const hasSelection =
      selection &&
      selection.rangeCount > 0 &&
      !selection.isCollapsed &&
      editorRef.current?.contains(selection.getRangeAt(0).commonAncestorContainer);

    if (!hasSelection && !allowLinkWithoutSelection) {
      setLinkMessage('Select text first to add link.');
      setShowLinkInput(false);
      return;
    }

    saveSelection();
    setLinkMessage('');
    setLinkUrl('');
    setShowLinkInput(true);
  };

  const applyLink = () => {
    const url = normalizeUrl(linkUrl);

    if (!isSafeUrl(url)) {
      setLinkMessage('Use http://, https://, mailto:, or tel: links only.');
      return;
    }

    editorRef.current?.focus();
    const restored = restoreSelection();
    const selection = window.getSelection();
    const hasSelection = restored && selection && !selection.isCollapsed;

    if (hasSelection) {
      document.execCommand('createLink', false, url);
    } else {
      if (!restored) moveCursorToEditorEnd();

      const safeHref = escapeAttribute(url);
      const safeText = escapeText(url);
      document.execCommand(
        'insertHTML',
        false,
        `<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${safeText}</a>`
      );
    }

    if (collapseSelectionAfterCommand) {
      collapseSelectionToEnd();
    } else {
      saveSelection();
    }
    syncValue();
    setShowLinkInput(false);
    setLinkUrl('');
  };

  return (
    <div className={className}>
      <div className={toolbarClassName} onMouseDown={(event) => event.preventDefault()}>
        <button onClick={() => runCommand('formatBlock', 'p')} type="button">
          P
        </button>
        <button onClick={() => runCommand('formatBlock', 'h2')} type="button">
          H2
        </button>
        <button onClick={() => runCommand('formatBlock', 'h3')} type="button">
          H3
        </button>
        <button onClick={() => runCommand('bold')} type="button">
          B
        </button>
        <button onClick={() => runCommand('italic')} type="button">
          I
        </button>
        <button onClick={() => runCommand('insertUnorderedList')} type="button">
          List
        </button>
        <button onClick={() => runCommand('insertOrderedList')} type="button">
          1. List
        </button>
        <button onClick={openLinkInput} type="button">
          Link
        </button>
        <button onClick={() => runCommand('unlink')} type="button">
          Unlink
        </button>
        {colorOptions.length ? (
          <span className="rich-editor-color-tools" aria-label="Text color">
            {colorOptions.map((option) => (
              <button
                aria-label={`Text color ${option.label}`}
                className="rich-editor-color-swatch"
                key={option.value}
                onClick={() => applyColor(option.value)}
                style={{ backgroundColor: option.value }}
                title={`Text color: ${option.label}`}
                type="button"
              />
            ))}
          </span>
        ) : null}
        {showPasteMode ? (
          <span className="rich-editor-paste-mode" aria-label="Paste mode">
            <button
              className={pasteMode === 'clean' ? 'is-active' : ''}
              onClick={() => setPasteMode('clean')}
              type="button"
            >
              Ctrl+V
            </button>
            <button
              className={pasteMode === 'plain' ? 'is-active' : ''}
              onClick={() => setPasteMode('plain')}
              type="button"
            >
              Ctrl+Shift+V
            </button>
          </span>
        ) : null}
      </div>

      {showLinkInput ? (
        <div className="job-rich-editor__link-row">
          <input
            autoFocus
            onChange={(event) => setLinkUrl(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                applyLink();
              }
            }}
            placeholder={linkInputPlaceholder}
            value={linkUrl}
          />
          <button onClick={applyLink} type="button">
            Apply
          </button>
          <button onClick={() => setShowLinkInput(false)} type="button">
            Cancel
          </button>
        </div>
      ) : null}

      {linkMessage ? <p className="job-rich-editor__message">{linkMessage}</p> : null}

      <div
        aria-label={ariaLabel}
        className={surfaceClassName}
        contentEditable
        onBlur={saveSelection}
        onInput={handleInput}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        onPaste={handlePaste}
        ref={editorRef}
        role="textbox"
        suppressContentEditableWarning
      />
    </div>
  );
}

export default RichTextEditor;
